import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAppSelector } from '@/store/hooks';
import { selectUser } from '@/store/features/userinfo/userinfoSlice';
import { selectPlaidAccounts } from '@/store/features/plaid/plaid-selectors';
import { useCostBasisEnabled, useFirmDataEnabled } from '@/hooks/useFirmDataEnabled';
import { CostBasisError, getBasisSummary, resolveBasisAccount } from './api';
import type { BasisSummary } from './types';

export type AccountBasis = {
  status: 'loading' | 'ready' | 'error' | 'unsupported';
  summary?: BasisSummary;
  apexId?: string;
  error?: CostBasisError;
};
type Context = {
  enabled: boolean;
  accounts: Record<string, AccountBasis>;
  load: (id: string, retry?: boolean) => void;
};
const BasisContext = createContext<Context>({ enabled: false, accounts: {}, load: () => {} });

const EMPTY: Record<string, AccountBasis> = {};
const noop = () => {};

/**
 * Holds cost-basis state for one investor session. A change of `session`
 * resets that state in place so the children (the whole Wealth page) stay
 * mounted; responses started under an earlier session are discarded.
 */
export function BasisSession({
  enabled = true,
  session = '',
  canonical,
  mapping,
  children,
}: {
  enabled?: boolean;
  session?: string;
  canonical: boolean;
  mapping: Record<string, string>;
  children: React.ReactNode;
}) {
  const [state, setState] = useState({ session, accounts: EMPTY });
  const accounts = state.session === session ? state.accounts : EMPTY;
  const requests = useRef(new Map<string, number>());
  const sequence = useRef(0);
  const mounted = useRef(true);
  const source = useRef({ session, canonical, mapping });
  useLayoutEffect(() => {
    source.current = { session, canonical, mapping };
  });
  // Runs before the children's effects request data for the new session.
  useLayoutEffect(() => {
    requests.current.clear();
  }, [session]);
  useEffect(() => {
    mounted.current = true;
    const pending = requests.current;
    return () => {
      mounted.current = false;
      pending.clear();
    };
  }, []);
  const load = useCallback(
    (id: string, retry = false) => {
      if (session !== source.current.session) return;
      if (requests.current.has(id) && !retry) return;
      const version = ++sequence.current;
      requests.current.set(id, version);
      const { canonical: isCanonical, mapping: ids } = source.current;
      const write = (value: AccountBasis) =>
        setState((old) => ({
          session,
          accounts: { ...(old.session === session ? old.accounts : EMPTY), [id]: value },
        }));
      write({ status: 'loading' });
      const publish = (value: AccountBasis) => {
        if (
          mounted.current &&
          source.current.session === session &&
          requests.current.get(id) === version
        )
          write(value);
      };
      void (async () => {
        try {
          const sourceId = isCanonical ? ids[id] : id;
          if (!sourceId) throw new CostBasisError(404);
          const apexId = await resolveBasisAccount(sourceId, isCanonical);
          if (!apexId) {
            publish({ status: 'unsupported' });
            return;
          }
          const summary = await getBasisSummary(apexId);
          publish({ status: 'ready', apexId, summary });
        } catch (error) {
          publish({
            status: 'error',
            error: error instanceof CostBasisError ? error : new CostBasisError(0),
          });
        }
      })();
    },
    [session],
  );
  const value = useMemo(
    () =>
      enabled ? { enabled: true, accounts, load } : { enabled: false, accounts: EMPTY, load: noop },
    [enabled, accounts, load],
  );
  return <BasisContext.Provider value={value}>{children}</BasisContext.Provider>;
}

export function CostBasisProvider({ children }: { children: React.ReactNode }) {
  const user = useAppSelector(selectUser);
  const plaid = useAppSelector(selectPlaidAccounts);
  const flag = useCostBasisEnabled(user?.client_id);
  const firm = useFirmDataEnabled(user?.client_id);
  const mapping = useMemo(() => {
    const result: Record<string, string> = {};
    const duplicates = new Set<string>();
    for (const account of plaid) {
      if (account.custodian_code !== 'APEX' || !account.custodian_account_id) continue;
      const id = account.custodian_account_id;
      if (result[id]) duplicates.add(id);
      result[id] = account.account_id;
    }
    for (const id of duplicates) delete result[id];
    return result;
  }, [plaid]);
  const enabled = Boolean(user && !flag.loading && flag.enabled && !firm.loading);
  // Never key or reshape the child tree: Wealth resets Plaid state on mount and
  // the snapshot then changes `mapping`, so remounting here would loop.
  const session = JSON.stringify([
    enabled,
    user?.client_id,
    user?.id,
    user?.cognito_id,
    firm.enabled,
    mapping,
  ]);
  return (
    <BasisSession enabled={enabled} session={session} canonical={firm.enabled} mapping={mapping}>
      {children}
    </BasisSession>
  );
}

export function useAccountBasis(accountId?: string) {
  const { enabled, accounts, load } = useContext(BasisContext);
  useEffect(() => {
    if (enabled && accountId) load(accountId);
  }, [enabled, load, accountId]);
  return {
    enabled: enabled,
    state: accountId ? accounts[accountId] : undefined,
    retry: () => {
      if (accountId) load(accountId, true);
    },
  };
}
export const useBasisEnabled = () => useContext(BasisContext).enabled;
